<template>
  <!-- ============================================================
       No.23 アンケート機能：管理者側「アンケート入力（作成/編集）」
       ============================================================ -->
  <div class="CmsSurveyEdit">
    <div class="contentsWidth mx-auto mt-4">
      <div>
        <p class="title mb-0">アンケート - 設定</p>
        <hr class="title">
      </div>

      <!-- 対象（年度＋助成種別） -->
      <div class="target-box my-3 px-3 py-2">
        <span class="bold mr-2">対象</span>
        {{ year }}年度 ／ {{ seriesLabel }}
      </div>

      <!-- 回答期間中（OPEN）または回答が存在する回はロック（基準は mst_survey_rounds＝回の状態＋回答件数） -->
      <b-alert :show="!editable" variant="warning">
        回答期間中、または回答がすでに存在するため、この回の設問は変更できません（閲覧のみ）。
      </b-alert>

      <div class="form-area mx-auto">
        <!-- 設問 -->
        <div class="form-row-item">
          <div class="form-label">
            <span class="badge badge-required mr-1">必須</span>設問
          </div>
          <div class="form-input">
            <b-form-textarea
              v-model="form.question"
              rows="3"
              :disabled="!editable"
              placeholder="設問を入力してください"/>
          </div>
        </div>

        <!-- 形式 -->
        <div class="form-row-item">
          <div class="form-label">
            <span class="badge badge-required mr-1">必須</span>形式
          </div>
          <div class="form-input">
            <b-form-select
              v-model="form.type"
              :options="typeOptions"
              :disabled="!editable"
              class="type-select"/>
          </div>
        </div>

        <!-- 必須/任意 -->
        <div class="form-row-item">
          <div class="form-label">回答の必須</div>
          <div class="form-input">
            <b-form-checkbox v-model="form.is_required" switch :disabled="!editable">
              この設問を必須にする
            </b-form-checkbox>
          </div>
        </div>

        <!-- 最大文字数（フリーテキストのみ・任意） -->
        <div class="form-row-item" v-if="isText">
          <div class="form-label">最大文字数</div>
          <div class="form-input">
            <b-form-input
              v-model="form.max_length"
              type="number"
              min="1"
              :disabled="!editable"
              class="num-input"/>
            <span class="ml-2 text-muted">文字（空欄なら上限なし）</span>
          </div>
        </div>

        <!-- 選択肢（選択系のみ） -->
        <div class="form-row-item" v-if="isChoice">
          <div class="form-label">
            <span class="badge badge-required mr-1">必須</span>選択肢
          </div>
          <div class="form-input">
            <div
              v-for="(opt, idx) in form.options" :key="idx"
              class="flex flexMiddle mb-2">
              <span class="opt-no mr-2">{{ idx + 1 }}.</span>
              <b-form-input
                v-model="opt.label"
                class="opt-input"
                :disabled="!editable"
                placeholder="選択肢を入力してください"/>
              <b-link
                v-if="editable"
                class="text-danger ml-3"
                @click="removeOption(idx)">削除</b-link>
            </div>
            <b-button v-if="editable" size="sm" variant="outline-primary" @click="addOption">
              ＋ 選択肢を追加
            </b-button>
          </div>
        </div>
      </div>

      <!-- ボタン -->
      <div class="mt-4 mb-5 flex flexCenter">
        <b-button class="btn btn-lg bold" @click="backToList">一覧に戻る</b-button>
        <b-button variant="primary" class="btn btn-lg ml-3 bold" :disabled="!editable" @click="save">登録</b-button>
        <b-button v-if="isEdit" class="btn btn-dark btn-lg ml-3 bold" :disabled="!editable" @click="remove">削除</b-button>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/modules/api';
import surveyConst from '@/constants/survey';

const TYPE = surveyConst.SURVEY_INPUT_TYPE;

export default {
  name: 'cmsSurveysEdit',
  data() {
    return {
      year: null,
      series: null,
      roundId: null,
      surveyId: null,
      seriesLabel: '',
      editable: true,
      seriesList: [],
      typeOptions: [
        { value: TYPE.TEXT, text: 'フリーテキスト' },
        { value: TYPE.DROPDOWN, text: 'ドロップダウン' },
        { value: TYPE.RADIO, text: 'ラジオボタン（単一選択）' },
        { value: TYPE.CHECKBOX, text: 'チェックボックス（複数選択）' },
      ],
      form: {
        question: '',
        type: TYPE.TEXT,
        is_required: false,
        max_length: 300,
        options: [
          { label: '' },
          { label: '' },
        ],
      },
    };
  },
  computed: {
    isEdit() {
      return !!this.surveyId;
    },
    isText() {
      return this.form.type === TYPE.TEXT;
    },
    isChoice() {
      return surveyConst.SURVEY_HAS_OPTIONS.includes(this.form.type);
    },
  },
  async created() {
    // data() の既定値（質問文が空・形式=フリーテキスト・最大文字数300・editable=true）で
    // 先に描画されてしまうため、覆わないと「一瞬 別の入力欄構成が見えてから正しい画面に
    // 切り替わる」（v-if="isText" / v-if="isChoice" で欄そのものが入れ替わる）。
    // ロック中の回では「削除」「選択肢を追加」ボタンも一瞬見えてしまう。
    // ★finally で必ず消す（消し忘れると画面が隠れたままになる）。
    this.$store.dispatch('page/onLoading');
    try {
      this.year = this.$route.query.year ? Number(this.$route.query.year) : null;
      this.series = this.$route.query.series ? Number(this.$route.query.series) : null;
      this.roundId = this.$route.query.roundId ? Number(this.$route.query.roundId) : null;
      this.surveyId = this.$route.query.surveyId || null;
      await this.fetchContext();
    } finally {
      this.$store.dispatch('page/offLoading');
    }
  },
  methods: {
    async fetchContext() {
      // 年度・助成種別リスト（ラベル用）＋ 編集可否（list の editable）
      const [initRes, listRes] = await Promise.all([
        api.send('/api/cms/survey/init'),
        api.send('/api/cms/survey/list', { year: this.year, series: this.series, roundId: this.roundId }),
      ]).catch(() => []);
      if (initRes) {
        this.seriesList = initRes.data.seriesList || [];
        const s = this.seriesList.find(x => x.id === this.series);
        this.seriesLabel = s ? s.name : '';
      }
      if (listRes) {
        this.editable = listRes.data.editable;
      }
      // 既存アンケートの読み込み
      if (this.surveyId) {
        const res = await api.send('/api/cms/survey/edit', { surveyId: this.surveyId }).catch(() => false);
        if (res && res.data.survey) {
          const sv = res.data.survey;
          this.form.question = sv.question_text;
          this.form.type = sv.input_type;
          this.form.is_required = !!sv.is_required;
          this.form.max_length = sv.max_length;
          const opts = (sv.options || []).map(o => ({ label: o.label }));
          this.form.options = opts.length > 0 ? opts : [{ label: '' }, { label: '' }];
        }
      }
    },
    addOption() {
      this.form.options.push({ label: '' });
    },
    removeOption(idx) {
      this.form.options.splice(idx, 1);
    },
    // クライアント側バリデーション
    validate() {
      const errs = [];
      if (!this.form.question || this.form.question.trim() === '') {
        errs.push('設問を入力してください。');
      }
      if (this.isText) {
        const ml = this.form.max_length;
        if (ml !== null && ml !== '' && (!/^\d+$/.test(String(ml)) || Number(ml) < 1)) {
          errs.push('最大文字数は1以上の整数で入力してください。');
        }
      }
      if (this.isChoice) {
        const labels = this.form.options
          .map(o => (o.label || '').trim())
          .filter(l => l !== '');
        if (labels.length < 1) {
          errs.push('選択肢を1件以上入力してください。');
        }
      }
      return errs;
    },
    async save() {
      const errs = this.validate();
      if (errs.length > 0) {
        await this.alert(errs.join('\n'));
        return;
      }
      const params = {
        surveyId: this.surveyId || null,
        year: this.year,
        series: this.series,
        survey_round_id: this.roundId,
        question_text: this.form.question,
        input_type: this.form.type,
        is_required: this.form.is_required ? 1 : 0,
        max_length: this.isText ? this.form.max_length : null,
        options: this.isChoice ? this.form.options.map(o => ({ label: o.label })) : [],
      };
      this.$store.dispatch('page/onWaiting');
      try {
        await api.send('/api/cms/survey/save', params);
      } catch (e) {
        this.$store.dispatch('page/offWaiting');
        const msgs = (e.response && e.response.data && e.response.data.errs) || ['保存に失敗しました。'];
        await this.alert(msgs.join('\n'));
        return;
      }
      this.$store.dispatch('page/offWaiting');
      await this.alert('アンケートを保存しました。', false);
      this.backToList();
    },
    async remove() {
      if (!await this.confirm('このアンケートを削除します。よろしいですか？')) {
        return;
      }
      this.$store.dispatch('page/onWaiting');
      try {
        await api.send('/api/cms/survey/destroy', { surveyId: this.surveyId });
      } catch (e) {
        this.$store.dispatch('page/offWaiting');
        const msgs = (e.response && e.response.data && e.response.data.errs) || ['削除に失敗しました。'];
        await this.alert(msgs.join('\n'));
        return;
      }
      this.$store.dispatch('page/offWaiting');
      await this.alert('アンケートを削除しました。', false);
      this.backToList();
    },
    backToList() {
      this.$router.push({
        path: '/cms/surveys/list',
        query: { year: this.year, series: this.series, roundId: this.roundId },
      });
    },
  },
};
</script>

<style scoped>
.target-box {
  background-color: #DDD;
  border-radius: 4px;
  display: block;
}
.form-area {
  max-width: 900px;
}
.form-row-item {
  display: flex;
  align-items: flex-start;
  padding: 14px 0;
  border-bottom: 1px solid #eee;
}
.form-label {
  width: 160px;
  min-width: 160px;
  font-weight: bold;
  padding-top: 6px;
}
.form-input {
  flex: 1;
}
.type-select {
  max-width: 320px;
}
.num-input {
  width: 140px;
  display: inline-block;
}
.opt-no {
  width: 24px;
  text-align: right;
  color: #555;
}
.opt-input {
  max-width: 420px;
}
/* 必須トグル（スイッチ）を丸み（ピル型）かつ大きめにする。
   ※グローバル overwriteBootstrap.css が .custom-control-label::before に
     border-radius:0 !important を当てて角ばらせているため、
     switch だけ詳細度＋!important で上書きする。 */
.CmsSurveyEdit >>> .custom-switch {
  padding-left: 3.5rem;
}
.CmsSurveyEdit >>> .custom-switch .custom-control-label {
  padding-top: 0.15rem;
}
.CmsSurveyEdit >>> .custom-switch .custom-control-label::before {
  left: -3.5rem;
  top: 0;
  width: 3rem;
  height: 1.6rem;
  border-radius: 1.6rem !important;
}
.CmsSurveyEdit >>> .custom-switch .custom-control-label::after {
  left: calc(-3.5rem + 3px);
  top: 3px;
  width: calc(1.6rem - 6px);
  height: calc(1.6rem - 6px);
  border-radius: 50% !important;
}
.CmsSurveyEdit >>> .custom-switch .custom-control-input:checked ~ .custom-control-label::after {
  transform: translateX(1.4rem);
}
</style>
